import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  CreditCard,
  Home,
  ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const statusConfig = {
  draft: {
    label: "Brouillon",
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: AlertCircle,
    description: "Cette commande est en cours de préparation."
  },
  pending: {
    label: "En attente de paiement",
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    icon: Clock,
    description: "Votre commande est en attente de paiement."
  },
  checkout_created: {
    label: "En attente de paiement",
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    icon: Clock,
    description: "Votre commande est en attente de paiement."
  },
  payment_initiated: {
    label: "Paiement en cours",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: AlertCircle,
    description: "Le paiement est en cours de traitement."
  },
  paid: {
    label: "Payé",
    color: "bg-green-100 text-green-800 border-green-200",
    icon: CheckCircle,
    description: "Le paiement a été reçu. Activation en cours."
  },
  paid_pending_property_data: {
    label: "Payé — informations logement à compléter",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: AlertCircle,
    description: "Renseignez les informations de votre/vos logement(s) pour activer la protection."
  },
  partially_completed: {
    label: "Partiellement complété",
    color: "bg-orange-100 text-orange-800 border-orange-200",
    icon: AlertCircle,
    description: "Certains logements restent à renseigner."
  },
  ready_to_activate: {
    label: "Prêt à activer",
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: CheckCircle,
    description: "Vos logements sont prêts, activation en cours."
  },
  active: {
    label: "Actif",
    color: "bg-green-100 text-green-800 border-green-200",
    icon: CheckCircle,
    description: "Votre protection est active."
  },
  completed: {
    label: "Complétée",
    color: "bg-green-100 text-green-800 border-green-200",
    icon: CheckCircle,
    description: "Commande payée et contrats activés."
  },
  expired: {
    label: "Expiré",
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: XCircle,
    description: "Cette commande a expiré."
  },
  cancelled: {
    label: "Annulé",
    color: "bg-red-100 text-red-800 border-red-200",
    icon: XCircle,
    description: "Cette commande a été annulée."
  },
  refunded: {
    label: "Remboursée",
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: XCircle,
    description: "Cette commande a été remboursée."
  } };

const OrderStatusPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/orders/${orderId}`,
        { withCredentials: true }
      );
      setOrder(data);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Commande non trouvée");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit" });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR" }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]"></div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const canPay = ["draft", "checkout_created", "pending", "payment_initiated"].includes(order.status);
  const amountHt = typeof order.amount_ht === "number" ? order.amount_ht : null;
  const amountTva = typeof order.amount_tva === "number" ? order.amount_tva : null;
  const amountTtc = typeof order.amount_ttc === "number" ? order.amount_ttc : order.total_amount;

  const nextSteps = {
    pending: [
      { icon: CreditCard, text: "Procédez au paiement pour activer votre protection" },
    ],
    payment_initiated: [
      { icon: Clock, text: "Finalisez votre paiement sur la page sécurisée" },
    ],
    paid: [
      { icon: FileText, text: "Vérifiez votre email pour le récapitulatif" },
      { icon: Home, text: "Complétez vos informations de logement" },
    ],
    active: [
      { icon: Home, text: "Ajoutez vos logements pour une protection complète" },
      { icon: FileText, text: "Consultez vos documents dans votre espace" },
    ],
    expired: [
      { icon: CreditCard, text: "Souscrivez à nouveau pour renouveler votre protection" },
    ],
    cancelled: [
      { icon: CreditCard, text: "Créez une nouvelle commande si vous souhaitez être protégé" },
    ] };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#19294C] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </Link>

          {/* Order Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="order-status-card">
            {/* Status Header */}
            <div className={`px-6 sm:px-8 py-6 ${status.color.split(' ')[0]} border-b ${status.color.split(' ')[2]}`}>
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 ${status.color.split(' ')[0]} rounded-xl flex items-center justify-center`}>
                  <StatusIcon className={`w-7 h-7 ${status.color.split(' ')[1]}`} />
                </div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                    {status.label}
                  </span>
                  <p className="mt-1 text-sm text-slate-600">{status.description}</p>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="p-6 sm:p-8 space-y-6">
              {/* Order Info */}
              <div>
                <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit'] mb-4">
                  Détails de la commande
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Numéro de commande</p>
                    <p className="font-medium text-slate-900">#{order.id.slice(0, 8)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date de création</p>
                    <p className="font-medium text-slate-900">{formatDate(order.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Type de compte</p>
                    <p className="font-medium text-slate-900 capitalize">{order.user_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Montant total</p>
                    <p className="font-medium text-slate-900">{formatPrice(amountTtc)} TTC</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Articles</h3>
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2">
                    <div>
                      <p className="font-medium text-slate-900">{item.description}</p>
                      <p className="text-sm text-slate-500">Quantité: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-slate-900">
                      {formatPrice(item.unit_price * item.quantity)}
                    </p>
                  </div>
                ))}
                <div className="pt-4 mt-4 border-t border-slate-100 space-y-2">
                  {amountHt !== null && (
                    <div className="flex justify-between items-baseline text-slate-600">
                      <span>Sous-total HT</span>
                      <span className="font-medium text-slate-900">{formatPrice(amountHt)}</span>
                    </div>
                  )}
                  {amountTva !== null && (
                    <div className="flex justify-between items-baseline text-slate-600">
                      <span>TVA (20%)</span>
                      <span className="font-medium text-slate-900">{formatPrice(amountTva)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-slate-100">
                    <p className="font-semibold text-slate-900">Total TTC</p>
                    <p className="text-xl font-bold text-[#19294C]">{formatPrice(amountTtc)}</p>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              {nextSteps[order.status] && nextSteps[order.status].length > 0 && (
                <div className="border-t border-slate-100 pt-6">
                  <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit'] mb-4">
                    Prochaines étapes
                  </h3>
                  <ul className="space-y-3">
                    {nextSteps[order.status].map((step, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                          <step.icon className="w-4 h-4 text-slate-600" />
                        </div>
                        <span className="text-slate-700">{step.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              {canPay && (
                <div className="border-t border-slate-100 pt-6">
                  <Link to="/checkout">
                    <Button 
                      className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                      data-testid="proceed-payment-btn"
                    >
                      Procéder au paiement
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderStatusPage;
